import React, { useState } from 'react';
import {
  useRemoveServiceMutation,
  useSaveServiceMutation,
  useServicesQuery,
  useServiceTypesQuery,
} from '@/queries/services';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import { FestivalHeader, Loading } from '../controls';
import Portal from '../modal/Portal';
import { SectionNavbar } from '../section-navbar';
import { AssignedSponsors } from '../sponsors';
import FilterableTable from '../table';
import { ServiceForm } from './';

export function Services() {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const festivalId = festival?.festivalId;

  const servicesQ = useServicesQuery(festivalId);
  const typesQ = useServiceTypesQuery();
  const saveMutation = useSaveServiceMutation(festivalId);
  const removeMutation = useRemoveServiceMutation(festivalId);

  const services = servicesQ.data ?? [];
  const serviceTypes = typesQ.data ?? {};
  const loading =
    festivalId == null ||
    servicesQ.isPending ||
    servicesQ.isFetching ||
    typesQ.isPending ||
    typesQ.isFetching;

  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState({});

  const buildColumnDefinitions = () => {
    var columnDefs = {};
    columnDefs.serviceName = {
      header: 'Service Name',
      filterable: true,
      sortable: true,
      showViewLink: false,
      handleClick: true,
    };
    columnDefs.type = {
      header: 'Type',
      filterable: false,
      sortable: false,
    };
    return columnDefs;
  };

  const onFileUpload = (fileInfo) => {
    const { filename, url } = fileInfo;
    setSelectedService((prev) => ({
      ...prev,
      photoURL: url,
      servicePhoto: filename,
    }));
  };

  const onFileDelete = () => {
    setSelectedService((prev) => ({
      ...prev,
      photoURL: null,
      servicePhoto: null,
    }));
  };

  const openModal = (service) => {
    const next = services.find((s) => s.serviceId === service.serviceId) || {};
    setSelectedService(next);
    setShowModal(true);
  };

  const handleLocationChange = (location) => {
    const updatedLocation = {
      ...selectedService.location,
      ...location,
      radius: 50,
    };
    setSelectedService((prev) => ({
      ...prev,
      location: updatedLocation,
    }));
  };

  const handleLocationNameChange = (locationName) => {
    const updatedLocation = {
      ...selectedService.location,
      locationName,
    };
    setSelectedService((prev) => ({
      ...prev,
      location: updatedLocation,
    }));
  };

  const handleSave = async (service) => {
    const updatedService = { ...selectedService, ...service };
    try {
      await saveMutation.mutateAsync(updatedService);
      setShowModal(false);
    } catch {
      /* mutation toast */
    }
  };

  const handleRemove = async () => {
    try {
      await removeMutation.mutateAsync(selectedService.serviceId);
      setSelectedService({});
      setShowModal(false);
    } catch {
      /* mutation toast */
    }
  };

  const serviceForm = (
    <ServiceForm
      types={serviceTypes}
      service={selectedService}
      handleSave={handleSave}
      handleRemove={handleRemove}
      handleLocationChange={handleLocationChange}
      handleLocationNameChange={handleLocationNameChange}
      onFileDelete={onFileDelete}
      onFileUpload={onFileUpload}
    />
  );

  const sponsorsComponent = (
    <AssignedSponsors
      objectId={selectedService.serviceId}
      objectType="service"
    />
  );

  const sections = [
    {
      title: 'Information',
      component: serviceForm,
    },
    {
      title: 'Sponsors',
      component: sponsorsComponent,
    },
  ];

  return (
    <>
      <FestivalHeader
        title="Services"
        showExit={true}
        showAdd={true}
        addTitle="Add Service"
        addAction={() => {
          setSelectedService({});
          setShowModal(true);
        }}
      />
      {loading ? (
        <Loading />
      ) : (
        <FilterableTable
          data={services}
          tableTitle=""
          columns={buildColumnDefinitions()}
          includeFilter={false}
          addNewRecordText="Service"
          editHandler={openModal}
          toggleModal={openModal}
        />
      )}

      <Portal
        header={
          selectedService
            ? selectedService.serviceName || 'Add New Service'
            : 'Add New Service'
        }
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <SectionNavbar navItems={sections} />
      </Portal>
    </>
  );
}
